import { format } from 'date-fns';
import * as ExcelJS from 'exceljs';
import { getPayoutPaymentBatchesForPayoutIds, type PayoutPaymentBatch } from '@/services/payoutPaymentService';
import type { RecaptureActivity } from '@/services/recaptureService';
import type { VolumeBonusActivity } from '@/services/loVolumeBonusService';
import { BONUS_AWARD_LABELS, type ProfitabilityBonus } from '@/services/loProfitabilityBonusService';
import type { ReportPeriod } from './date-range/reportPeriod';
import { createComponentLogger } from '@/utils/logger';
import {
  buildEarningLedger, bonusPeriodContribution, computeDisplayedEarningLoProfitability, profitabilityReconciliation,
  computeEarningConcessionBreakdown, computeEarningNetLoLocked, computeEarningLocrBreakdown,
  computeNoLoCompMarginReconciliation, getEarningLoCompensationVarianceBasis, computeEarningPricingExcess,
  earningBalance, isPreJulyResetProfitabilityRow, channelLabel,
  type CommissionEarning, type CommissionContextRow, type AdjustedCommissionTotals, type RosterRow, type ConcessionImpact
} from './myCommissionReportModel';

const log = createComponentLogger('CommissionWorkbook', 'reports');
const usd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const bps = (n: number): string => `${n.toFixed(1)} bps`;

const purchasedDateFor = (e: CommissionEarning): string | null | undefined =>
  e.purchase_date ?? e.finalized_at;

const IMPACT_LABELS: Record<ConcessionImpact, string> = {
  reduces_lo_payout: 'Reduces LO payout',
  org_absorbed: 'Organization absorbed',
  pricing_only_borrower_credit: 'Borrower credit (pricing only)'
};

function compBreakdown(e: CommissionEarning): { amount: number; effectiveBps: number } | null {
  if (e.payout_type === 'banked' && e.comp_basis) {
    const amount =
      e.comp_basis.compensation_type === 'flat_fee'
        ? e.comp_basis.compensation_value
        : (e.loan_amount * e.comp_basis.compensation_value) / 100;
    const effectiveBps =
      e.comp_basis.compensation_type === 'flat_fee'
        ? e.loan_amount > 0
          ? (e.comp_basis.compensation_value / e.loan_amount) * 10000
          : 0
        : e.comp_basis.compensation_value * 100;
    return { amount, effectiveBps };
  }
  return null;
}

// ── Shared Excel export ──
export interface ExportOptions {
  recaptureRecords: RecaptureActivity[] | null;
  recipientId?: string;
  volumeBonusActivity?: VolumeBonusActivity[];
  bonuses?: ProfitabilityBonus[];
  filename: string;
  period: ReportPeriod;
  earnings: CommissionEarning[];
  context: CommissionContextRow[];
  /** Admin all-records: add Recipient/Source columns + roster Summary + flat Records sheet. */
  participants?: boolean;
  recipientNameFor?: (userId: string | null | undefined) => string;
  /** Single-user basis label (self / single-user export). */
  basisLabel?: string;
  totals?: AdjustedCommissionTotals;
  rosterRows?: RosterRow[];
  maskResetPricingEvidence?: boolean;
}

export async function downloadCommissionWorkbook(opts: ExportOptions) {
  const { earnings, context, participants = false, recipientNameFor, period, maskResetPricingEvidence = false } = opts;
  const wb = new ExcelJS.Workbook();
  const recip = (uid: string | null | undefined) => recipientNameFor?.(uid) ?? '';
  let paymentBatches: PayoutPaymentBatch[] = [];
  try {
    paymentBatches = await getPayoutPaymentBatchesForPayoutIds(
      earnings.map((earning) => earning.payout_id)
    );
  } catch (error) {
    log.warn('Payment ledger could not be included in commission export', {}, error as Error);
  }

  // Summary sheet
  const summary = wb.addWorksheet('Summary');
  if (opts.rosterRows) {
    summary.columns = [
      { header: 'User', key: 'user', width: 26 },
      { header: 'Status', key: 'status', width: 12 },
      { header: 'Records', key: 'records', width: 10 },
      { header: 'Earned', key: 'earned', width: 14 },
      { header: 'Paid', key: 'paid', width: 14 },
      { header: 'Owed', key: 'owed', width: 14 },
      { header: 'LO Profitability', key: 'profit', width: 16 },
      { header: 'Profitability bonuses', key: 'bonuses', width: 22 },
      { header: 'Recaptured from profitability', key: 'profitRecaptured', width: 30 },
      { header: 'Profitability reconciliation', key: 'profitReconciliation', width: 80 }
    ];
    for (const r of opts.rosterRows) {
      summary.addRow({
        user: r.name ?? '',
        status: r.employment_status ?? '',
        records: r.record_count,
        earned: r.earned,
        paid: r.paid,
        owed: r.owed,
        profit: r.loProfitability,
        bonuses: r.profitabilityBonuses ?? 0,
        profitRecaptured: r.profitabilityRecaptured ?? 'Recapture activity unavailable',
        profitReconciliation: opts.recaptureRecords === null ? 'Recapture activity unavailable' : profitabilityReconciliation(earnings.filter(e => e.lo_user_id === r.user_id), opts.recaptureRecords, period, r.user_id)
      });
    }
  } else {
    summary.columns = [
      { header: 'Metric', key: 'metric', width: 28 },
      { header: 'Value', key: 'value', width: 24 }
    ];
    const t = opts.totals;
    summary.addRows([
      { metric: 'Period', value: `${format(period.from, 'MM/dd/yyyy')} – ${format(period.to, 'MM/dd/yyyy')}` },
      ...(opts.basisLabel ? [{ metric: 'Comp basis', value: opts.basisLabel }] : []),
      { metric: 'Total Earned', value: t?.totalEarned ?? 0 },
      { metric: 'Total Paid', value: t?.totalPaid ?? 0 },
      { metric: 'Recovered from commissions', value: t?.totalRecaptured ?? 0 },
      { metric: 'Outstanding (Owed)', value: t?.outstanding ?? 0 },
      { metric: 'LO Profitability', value: t?.loProfitability ?? 0 },
      { metric: 'Profitability bonuses', value: t?.profitabilityBonuses ?? 0 },
      { metric: 'Volume bonus commitments / returns', value: t?.profitabilityVolumeBonusAdjustment ?? 0 },
      { metric: 'Recaptured from profitability', value: t?.profitabilityRecaptured ?? 'Recapture activity unavailable' },
      { metric: 'Profitability reconciliation', value: opts.recaptureRecords === null ? 'Recapture activity unavailable' : profitabilityReconciliation(earnings, opts.recaptureRecords, period, opts.recipientId) },
      { metric: 'Concessions', value: t?.concessionsTotal ?? 0 },
      { metric: 'Concession Files', value: t?.concessionFileCount ?? 0 },
      { metric: 'Records', value: t?.loanCount ?? 0 }
    ]);
  }

  const bonusSheet = wb.addWorksheet('Profitability Bonuses');
  bonusSheet.columns = [
    { header: 'Recipient', key: 'recipient', width: 26 },
    { header: 'Award', key: 'award', width: 36 },
    { header: 'Winning month', key: 'month', width: 16 },
    { header: 'Effective date', key: 'effective', width: 16 },
    { header: 'Amount', key: 'amount', width: 16 },
    { header: 'Status', key: 'status', width: 14 },
    { header: 'Period contribution', key: 'contribution', width: 22 },
    { header: 'Bonus ID', key: 'id', width: 38 }
  ];
  for (const bonus of opts.bonuses ?? []) {
    bonusSheet.addRow({ recipient: bonus.recipient_name ?? recip(bonus.recipient_id), award: BONUS_AWARD_LABELS[bonus.award_type],
      month: bonus.award_month, effective: bonus.effective_date, amount: bonus.amount,
      status: bonus.status === 'voided' ? 'Voided' : bonus.is_effective ? 'Active' : 'Scheduled',
      contribution: bonusPeriodContribution(bonus, period), id: bonus.id });
  }
  bonusSheet.getColumn('amount').numFmt = '$#,##0.00';
  bonusSheet.getColumn('contribution').numFmt = '$#,##0.00';

  // Flat records sheet
  const records = wb.addWorksheet(participants ? 'Commission Records' : 'Commission Detail');
  records.columns = [
    ...(participants ? [{ header: 'Recipient', key: 'recipient', width: 22 }, { header: 'Source officer', key: 'source', width: 20 }] : []),
    { header: 'Loan #', key: 'loan', width: 16 },
    { header: 'Borrower', key: 'borrower', width: 22 },
    { header: 'Funded', key: 'funded', width: 12 },
    { header: 'Purchased', key: 'purchased', width: 12 },
    { header: 'Latest Paid Date', key: 'paidDate', width: 16 },
    { header: 'Channel', key: 'channel', width: 12 },
    { header: 'Company Concession ($)', key: 'companyConcession', width: 24 },
    { header: 'Company Concession (bps)', key: 'companyConcessionBps', width: 26 },
    { header: 'Comp', key: 'comp', width: 12 },
    { header: 'Loan Amount', key: 'amount', width: 14 },
    { header: 'Net Earned', key: 'net', width: 14 },
    { header: 'Paid Amount', key: 'paid', width: 14 },
    { header: 'Recaptured Amount', key: 'recaptured', width: 18 },
    { header: 'Balance', key: 'balance', width: 14 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'LO Profitability', key: 'profit', width: 16 }
  ];
  for (const e of earnings) {
    const maskPricingEvidence = maskResetPricingEvidence && isPreJulyResetProfitabilityRow(e);
    const concession = maskPricingEvidence
      ? { totalConcessionDollars: 0 }
      : computeEarningConcessionBreakdown(e);
    const concessionBps =
      concession.totalConcessionDollars > 0.01 && e.loan_amount > 0
        ? (concession.totalConcessionDollars / e.loan_amount) * 10000
        : null;
    const comp = compBreakdown(e);
    records.addRow({
      ...(participants ? { recipient: recip(e.lo_user_id), source: e.source_officer_name ?? '' } : {}),
      loan: e.loan_number,
      borrower: e.borrower_name ?? '',
      funded: e.fund_date ?? '',
      purchased: purchasedDateFor(e) ?? '',
      paidDate: e.payment_date ?? '',
      channel: channelLabel(e.payout_type),
      companyConcession: concession.totalConcessionDollars || '',
      companyConcessionBps: concessionBps ?? '',
      comp: comp ? `${usd(comp.amount)} (${bps(comp.effectiveBps)})` : '',
      amount: e.payout_type === 'volume_bonus' ? '' : e.loan_amount,
      net: e.net_amount,
      paid: e.payment_amount ?? 0,
      recaptured: e.recaptured_amount ?? 0,
      balance: earningBalance(e),
      status: e.status,
      profit: computeDisplayedEarningLoProfitability(e, opts.recaptureRecords, opts.recipientId) ?? ''
    });
  }

  if (opts.volumeBonusActivity?.length) {
    const activity = wb.addWorksheet('Volume Bonus Commitments');
    activity.columns = [
      { header: 'Recipient', key: 'recipient', width: 26 },
      { header: 'Pacific business date', key: 'date', width: 24 },
      { header: 'Activity', key: 'kind', width: 18 },
      { header: 'Profitability adjustment', key: 'amount', width: 26 },
      { header: 'Agreement ID', key: 'plan', width: 38 }
    ];
    for (const event of opts.volumeBonusActivity) {
      activity.addRow({ recipient: recip(event.recipient_id), date: event.date, kind: event.kind,
        amount: event.amount, plan: event.plan_id });
    }
    activity.getColumn('amount').numFmt = '$#,##0.00;[Red]-$#,##0.00';
  }

  if (paymentBatches.length > 0) {
    const paymentSheet = wb.addWorksheet('Payment Checks');
    paymentSheet.columns = [
      ...(participants ? [{ header: 'Recipient', key: 'recipient', width: 22 }] : []),
      { header: 'Check #', key: 'checkNumber', width: 18 },
      { header: 'Payment Date', key: 'paymentDate', width: 14 },
      { header: 'Check Total', key: 'checkTotal', width: 16 },
      { header: 'Loan #', key: 'loan', width: 16 },
      { header: 'Borrower', key: 'borrower', width: 22 },
      { header: 'Allocation', key: 'allocation', width: 16 },
      { header: 'Status', key: 'status', width: 12 },
      { header: 'Legacy', key: 'legacy', width: 12 },
      { header: 'Void Reason', key: 'voidReason', width: 30 }
    ];
    for (const batch of paymentBatches) {
      for (const allocation of batch.allocations) {
        paymentSheet.addRow({
          ...(participants ? { recipient: batch.recipientName } : {}),
          checkNumber: batch.checkNumber,
          paymentDate: batch.paymentDate,
          checkTotal: batch.totalAmount,
          loan: allocation.loanNumber,
          borrower: allocation.borrowerName || '',
          allocation: allocation.amount,
          status: batch.voidedAt ? 'Voided' : 'Active',
          legacy: batch.historyComplete ? 'No' : 'Yes',
          voidReason: batch.voidReason || ''
        });
      }
    }
    paymentSheet.getColumn('checkTotal').numFmt = '$#,##0.00;[Red]-$#,##0.00';
    paymentSheet.getColumn('allocation').numFmt = '$#,##0.00;[Red]-$#,##0.00';
  }

  // Payout ledger
  const ledgerSheet = wb.addWorksheet('Payout Ledger');
  ledgerSheet.columns = [
    ...(participants ? [{ header: 'Recipient', key: 'recipient', width: 22 }] : []),
    { header: 'Loan #', key: 'loan', width: 16 },
    { header: 'Channel', key: 'channel', width: 12 },
    { header: 'Line', key: 'line', width: 26 },
    { header: 'Type', key: 'type', width: 14 },
    { header: '% of loan', key: 'pct', width: 12 },
    { header: 'Amount', key: 'amount', width: 14 }
  ];
  for (const e of earnings) {
    for (const line of buildEarningLedger(e).lines) {
      ledgerSheet.addRow({
        ...(participants ? { recipient: recip(e.lo_user_id) } : {}),
        loan: e.loan_number,
        channel: channelLabel(e.payout_type),
        line: line.label,
        type: line.type,
        pct: line.percent ?? '',
        amount: line.amount
      });
    }
  }

  // Pricing evidence
  const evidence = wb.addWorksheet('Pricing Evidence');
  evidence.columns = [
    ...(participants ? [{ header: 'Recipient', key: 'recipient', width: 22 }] : []),
    { header: 'Loan #', key: 'loan', width: 16 },
    { header: 'Total Price Locked', key: 'buy', width: 18 },
    { header: 'Discount (pts)', key: 'disc', width: 14 },
    { header: 'Discount Provenance', key: 'discprov', width: 20 },
    { header: 'Borrower-Paid Comp (pts)', key: 'bpcPts', width: 24 },
    { header: 'Borrower-Paid Comp ($)', key: 'bpcDollars', width: 22 },
    { header: 'Borrower-Paid Comp Provenance', key: 'bpcprov', width: 30 },
    { header: 'LO Comp (bps)', key: 'locomp', width: 14 },
    { header: 'LO Comp Plan ($)', key: 'loCompPlan', width: 18 },
    { header: 'LO Comp Built In ($)', key: 'loCompBuiltIn', width: 21 },
    { header: 'LO Comp Variance ($)', key: 'loCompVariance', width: 21 },
    { header: 'LOCR (pts)', key: 'locr', width: 12 },
    { header: 'LOCR ($)', key: 'locrDollars', width: 14 },
    { header: 'LOCR Applied to Net LO Locked', key: 'locrApplied', width: 28 },
    { header: 'Lender Credit', key: 'lc', width: 14 },
    { header: 'Cure', key: 'cure', width: 12 },
    { header: 'Actual Concession (pts)', key: 'conc', width: 22 },
    { header: 'Actual Concession ($)', key: 'actualConcession', width: 22 },
    { header: 'Concession Provenance', key: 'concprov', width: 20 },
    { header: 'Concession Impact', key: 'concimpact', width: 26 },
    { header: 'Net LO Locked', key: 'nll', width: 14 },
    { header: 'Pricing Excess', key: 'pricingExcess', width: 16 },
    { header: 'Implied Concession', key: 'impliedConcession', width: 20 },
    { header: 'Total Concession', key: 'totalConcession', width: 20 },
    { header: 'Pass-Through Reimb Excluded', key: 'passThroughReimb', width: 28 },
    { header: 'LO Paid Amount', key: 'loPaid', width: 16 },
    { header: 'Gross Profitability', key: 'grossProfit', width: 18 },
    { header: 'LO Profitability', key: 'profit', width: 16 }
  ];
  for (const e of earnings) {
    const pe = e.pricing_evidence;
    if (!pe || e.payout_type === 'volume_bonus') {
      continue;
    }
    if (maskResetPricingEvidence && isPreJulyResetProfitabilityRow(e)) {
      continue;
    }
    // Finalized routed recons carry a stored signed net + gross. The recomputed
    // Net LO Locked / pricing-excess assume the credit hit profitability, so they
    // diverge once a credit is routed to Base Comp — blank them and show the
    // authoritative stored gross/net instead (matches the detail + card).
    const hasStored = e.payout_type === 'banked' && e.finalized_lo_profitability_net !== null && e.finalized_lo_profitability_net !== undefined;
    const nll = computeEarningNetLoLocked(e);
    const locr = computeEarningLocrBreakdown(e);
    const noLoCompRecon = computeNoLoCompMarginReconciliation(e);
    const concessionBreakdown = computeEarningConcessionBreakdown(e);
    const loCompVarianceBasis = getEarningLoCompensationVarianceBasis(e);
    evidence.addRow({
      ...(participants ? { recipient: recip(e.lo_user_id) } : {}),
      loan: e.loan_number,
      buy: pe.buy_price,
      disc: pe.discount_points,
      discprov: pe.discount_provenance ?? '',
      bpcPts: pe.bpc_bps / 100,
      bpcDollars: noLoCompRecon?.borrowerPaidCompDollars ?? (pe.bpc_bps / 10000) * e.loan_amount,
      bpcprov: pe.bpc_provenance ?? '',
      locomp: pe.lo_comp_bps,
      loCompPlan: loCompVarianceBasis?.planAmount ?? '',
      loCompBuiltIn: loCompVarianceBasis?.builtInAmount ?? '',
      loCompVariance: loCompVarianceBasis?.varianceAmount ?? '',
      locr: locr?.displayPoints || '',
      locrDollars: locr?.displayPoints ? (e.loan_amount * locr.displayPoints) / 100 : '',
      locrApplied: locr?.appliedPoints ? 'Yes' : locr?.explicitPoints ? 'No - no LO comp margin' : 'No',
      lc: pe.lender_credit_dollars,
      cure: pe.cure_dollars,
      conc: pe.concession_points,
      actualConcession: concessionBreakdown.actualConcessionDollars || '',
      concprov: pe.concessions.map((c) => c.provenance).join(', '),
      concimpact: pe.concessions.map((c) => IMPACT_LABELS[c.impact]).join(', '),
      nll: hasStored ? '' : (nll?.displayedNetLoLocked ?? ''),
      pricingExcess: hasStored ? '' : (computeEarningPricingExcess(e) ?? ''),
      impliedConcession: concessionBreakdown.impliedConcessionDollars || '',
      totalConcession: concessionBreakdown.totalConcessionDollars || '',
      passThroughReimb: noLoCompRecon?.passThroughReimbursements || '',
      loPaid: noLoCompRecon?.loPaidAmount ?? '',
      grossProfit: hasStored ? (e.finalized_lo_profitability_gross ?? '') : '',
      profit: computeDisplayedEarningLoProfitability(e, opts.recaptureRecords, opts.recipientId) ?? ''
    });
  }

  // Splits (context) — only when there are splits to show (never for LO self
  // exports, which pass context: []). Overrides paid out are confidential to LOs.
  if (context.length > 0) {
    const splits = wb.addWorksheet('Splits (context)');
    splits.columns = [
      ...(participants ? [{ header: 'Source officer', key: 'source', width: 22 }] : []),
      { header: 'Loan #', key: 'loan', width: 16 },
      { header: 'Recipient', key: 'recipient', width: 24 },
      { header: 'Type', key: 'type', width: 12 },
      { header: 'Amount', key: 'amount', width: 14 },
      { header: 'Status', key: 'status', width: 12 }
    ];
    for (const c of context) {
      splits.addRow({
        ...(participants ? { source: recip(c.source_officer_id) } : {}),
        loan: c.loan_number,
        recipient: c.recipient_name,
        type: channelLabel(c.payout_type),
        amount: c.net_amount,
        status: c.status
      });
    }
  }

  wb.worksheets.forEach((ws) => {
    const header = ws.getRow(1);
    header.font = { bold: true };
    header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } };
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = opts.filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const periodFile = (p: ReportPeriod, prefix: string) =>
  `${prefix}-${format(p.from, 'yyyy-MM-dd')}-to-${format(p.to, 'yyyy-MM-dd')}.xlsx`;
